import React from 'react';
import { useTranslation } from 'react-i18next';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import type { RatingDisplayProps, RatingBreakdownProps } from '../types/RatingDisplay.types';

export const RatingDisplay: React.FC<RatingDisplayProps> = ({
  rating,
  maxRating = 5,
  size = 'md',
  showNumber = true,
  totalReviews,
  onClick,
  readonly = true,
}) => {
  const { t } = useTranslation();
  const [hoveredRating, setHoveredRating] = React.useState<number | null>(null);

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const displayRating = hoveredRating !== null ? hoveredRating : rating;

  const handleStarClick = (starRating: number) => {
    if (!readonly && onClick) {
      onClick(starRating);
    }
  };

  const handleStarHover = (starRating: number) => {
    if (!readonly) {
      setHoveredRating(starRating);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoveredRating(null);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div
        className="flex items-center gap-0.5"
        onMouseLeave={handleMouseLeave}
      >
        {Array.from({ length: maxRating }, (_, index) => {
          const starRating = index + 1;
          const isFilled = starRating <= Math.round(displayRating);

          return (
            <button
              key={starRating}
              type="button"
              onClick={() => handleStarClick(starRating)}
              onMouseEnter={() => handleStarHover(starRating)}
              disabled={readonly}
              className={`
                ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}
                transition-transform duration-150
                focus:outline-none
              `}
              aria-label={`${starRating} stars`}
            >
              {isFilled ? (
                <StarIcon
                  className={`${sizeClasses[size]} text-yellow-400`}
                />
              ) : (
                <StarOutlineIcon
                  className={`${sizeClasses[size]} ${readonly ? 'text-titan' : 'text-tiber-300'}`}
                />
              )}
            </button>
          );
        })}
      </div>

      {showNumber && (
        <div className="flex items-baseline gap-1">
          <span className={`font-semibold text-tiber-900 ${textSizeClasses[size]}`}>
            {rating.toFixed(1)}
          </span>
          {totalReviews !== undefined && (
            <span className="text-xs text-tiber-600">
              ({totalReviews} {totalReviews === 1 ? t("ratingDisplay.review") : t("ratingDisplay.reviews")})
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const RatingBreakdown: React.FC<RatingBreakdownProps> = ({
  breakdown,
  totalReviews,
}) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      {breakdown.map((item) => (
        <div key={item.rating} className="flex items-center gap-3">
          <div className="flex items-center gap-1 w-16">
            <span className="text-sm font-medium text-tiber-900">
              {item.rating}
            </span>
            <StarIcon className="h-4 w-4 text-yellow-400" />
          </div>
          
          <div className="flex-1">
            <div className="h-2 bg-tiber-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-yellow-400 rounded-full transition-all duration-300"
                style={{ width: `${item.percentage}%` }}
              />
            </div>
          </div>
          
          <span className="text-sm text-tiber-600 w-12 text-right">
            {item.count}
          </span>
        </div>
      ))}
      
      {totalReviews === 0 && (
        <p className="text-sm text-tiber-500 text-center py-4">
          {t("ratingDisplay.noReviewsYet")}
        </p>
      )}
    </div>
  );
};

